import { TOptions } from "i18next";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { useCompany } from "@/features/company/hooks/use-company";
import { InitCardTransactionResponse } from "@/features/company/types";
import { MonextWidget } from "@/features/payment/monext-widget";
import { MonextStatus } from "@/features/payment/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { extractErrorDescription } from "@/utils/internal-code-handler";

type CompanyPaymentCardProps = {
  onFinish: () => void;
};

export const CompanyPaymentCard = ({ onFinish }: CompanyPaymentCardProps) => {
  const translation = useTranslation(["pass", "error"]);
  const { t } = translation;
  const tr = (key: string, options?: TOptions) => t(`companyPayment.payment_card.${key}`, options);
  const { getLanguage } = usei18n();
  const [transactionStatus, setTransactionStatus] = useState<MonextStatus | null>(null);

  const { initCardTransactionMutation } = useCompany();
  const { mutateAsync: initCardTransaction, isPending: isLoading } = initCardTransactionMutation();

  const [cardTransactionData, setCardTransactionData] =
    useState<InitCardTransactionResponse | null>(null);

  useEffect(() => {
    getCardTransactionData();
  }, []);

  useEffect(() => {
    if (transactionStatus === MonextStatus.SUCCESS) {
      onFinish();
    }
  }, [transactionStatus]);

  const getCardTransactionData = async () => {
    try {
      setTransactionStatus(null);
      setCardTransactionData(null);

      const response = await initCardTransaction({ lang: getLanguage() });
      setCardTransactionData(response);
    } catch (e) {
      toast.error(extractErrorDescription(e, translation));
    }
  };

  if (isLoading || !cardTransactionData) {
    return (
      <div className="my-5 flex justify-center rounded-tl-md rounded-br-md border border-primary bg-primary/10 px-5 py-8 dark:bg-primary-700">
        <div className="my-10 flex w-full justify-center">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="my-5 flex w-full justify-center rounded-tl-md rounded-br-md border border-primary bg-primary/10 px-5 py-8 dark:bg-primary-700">
        <MonextWidget
          sessionId={cardTransactionData.sessionId}
          referenceId={cardTransactionData.reference}
          onPaymentComplete={(status) => setTransactionStatus(status)}
        />
      </div>

      {transactionStatus && (
        <>
          {[MonextStatus.FAILED, MonextStatus.INTERNAL_ERROR].includes(transactionStatus) && (
            <div className="flex w-full justify-center">
              <Button className="w-auto" onClick={() => getCardTransactionData()}>
                {tr("retry")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
